import { useEffect, useRef, useState } from 'react';
import { MagnifyingGlass, X } from 'shared/icons';
import { useTokens } from './tokens';

/**
 * FinderOverlay — substitui o Cmd/Ctrl+F nativo do browser por um campo de
 * busca centralizado da página /operacoes.
 *
 * Comportamento:
 *   - Abre com Ctrl+F (handler vive no caller). Pré-preenche com a busca
 *     atual pra dar continuidade ao último termo.
 *   - Enter: confirma → propaga pro `onCommit` (caller sincroniza com o
 *     input principal do header) e fecha.
 *   - Escape: fecha sem confirmar.
 *   - Click no backdrop: fecha sem confirmar.
 *   - Auto-focus no input ao abrir.
 */
export function FinderOverlay({ open, initialValue = '', onCommit, onClose }) {
    const T = useTokens();
    const [value, setValue] = useState(initialValue);
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setValue(initialValue);
            const t = setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 0);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [open, initialValue]);

    if (!open) return null;

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onCommit?.(value);
            onClose?.();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            onClose?.();
        }
    };

    return (
        <>
            <button
                type="button"
                aria-label="Fechar busca"
                onClick={onClose}
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.45)',
                    border: 'none',
                    cursor: 'default',
                    padding: 0,
                    zIndex: 9998,
                    animation: 'opFinderFadeIn 0.12s ease-out',
                }}
            />
            <div
                style={{
                    position: 'fixed',
                    top: '20%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 'min(560px, 90vw)',
                    background: T.bg,
                    border: `1px solid ${T.lineMid}`,
                    borderRadius: 8,
                    boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
                    padding: 14,
                    zIndex: 9999,
                    animation: 'opFinderSlideIn 0.16s ease-out',
                }}
                data-faid="Finder_Overlay_Operacoes"
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '6px 4px 10px',
                        borderBottom: `1px solid ${T.line}`,
                        marginBottom: 10,
                    }}
                >
                    <MagnifyingGlass size={16} color={T.muted} />
                    <span
                        style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: T.muted,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            flex: 1,
                        }}
                    >
                        Buscar produtos · Enter aplica · Esc fecha
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Fechar"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: T.muted,
                            display: 'inline-flex',
                            padding: 4,
                            borderRadius: 4,
                        }}
                    >
                        <X size={14} />
                    </button>
                </div>
                <input
                    ref={inputRef}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="SKU, nome, marca, MLB, tag, Bling ID..."
                    style={{
                        width: '100%',
                        background: T.bgSoft,
                        border: `1px solid ${T.line}`,
                        borderRadius: 6,
                        color: T.ink,
                        fontFamily: T.sans,
                        fontSize: 15,
                        padding: '10px 12px',
                        outline: 'none',
                    }}
                    data-faid="Finder_Overlay_Input_Operacoes"
                />
                <style>{`
                    @keyframes opFinderFadeIn {
                        from { opacity: 0; }
                        to   { opacity: 1; }
                    }
                    @keyframes opFinderSlideIn {
                        from { transform: translate(-50%, -20px); opacity: 0; }
                        to   { transform: translate(-50%, 0);     opacity: 1; }
                    }
                `}</style>
            </div>
        </>
    );
}
